import { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Spin } from '@bancobarigui/barihaus';
import { Input, message as antdMessage, Drawer, FloatButton } from 'antd';
import {
  SendOutlined,
  MessageOutlined,
  SwapOutlined,
  AudioOutlined,
  AudioMutedOutlined,
  VideoCameraOutlined,
  PhoneOutlined,
  UserOutlined,
} from '@ant-design/icons';
import DailyIframe from '@daily-co/daily-js';
import { ENV } from '@/config/environment';

interface ChatMessage {
  sender: string;
  content: string;
  timestamp: string;
  fromSelf: boolean;
}

export function GuestVideoCall() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const guestName = searchParams.get('name') || 'Convidado';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [roomUrl, setRoomUrl] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState<string>('user');

  // Custom video states
  const [localVideoTrack, setLocalVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [remoteVideoTrack, setRemoteVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [remoteAudioTrack, setRemoteAudioTrack] = useState<MediaStreamTrack | null>(null);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [hasRemoteParticipant, setHasRemoteParticipant] = useState(false);

  const dailyContainerRef = useRef<HTMLDivElement>(null);
  const dailyCallRef = useRef<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);

  // Vincular tracks aos elementos <video>/<audio>
  useEffect(() => {
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = localVideoTrack
        ? new MediaStream([localVideoTrack])
        : null;
    }
  }, [localVideoTrack]);

  useEffect(() => {
    const el = remoteVideoRef.current;
    if (el && remoteVideoTrack) {
      el.srcObject = new MediaStream([remoteVideoTrack]);
      el.muted = true;
      el.play().catch(() => {});
    } else if (el) {
      el.srcObject = null;
    }
  }, [remoteVideoTrack]);

  useEffect(() => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = remoteAudioTrack
        ? new MediaStream([remoteAudioTrack])
        : null;
    }
  }, [remoteAudioTrack]);

  useEffect(() => {
    if (!sessionId) {
      setError('ID da sessão não fornecido');
      setLoading(false);
      return;
    }

    fetchRoomCredentials();
  }, [sessionId]);

  const fetchRoomCredentials = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `${ENV.INSPECTION_SERVICE_URL}/api/video-sessions/${sessionId}/room-credentials`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Não foi possível acessar a sessão de vídeo');
      }

      const data = await response.json();

      if (!data.hasAccess) {
        throw new Error(data.accessDeniedReason || 'Acesso negado');
      }

      setRoomUrl(data.roomUrl);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar sessão de vídeo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!roomUrl) return;

    initializeDaily();

    return () => {
      if (dailyCallRef.current) {
        try {
          dailyCallRef.current.destroy();
        } catch {
          // Ignore errors during cleanup
        }
      }
    };
  }, [roomUrl]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    const checkIfMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobile(isMobileDevice || isTouchDevice);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const initializeDaily = async () => {
    if (!roomUrl) return;

    try {
      const callObject = DailyIframe.createCallObject({
        url: roomUrl,
        userName: guestName,
        subscribeToTracksAutomatically: true,
        dailyConfig: {
          userMediaAudioConstraints: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        },
      });

      dailyCallRef.current = callObject;

      callObject.on('joined-meeting', () => {
        setLoading(false);
        const local = callObject.participants().local;
        const videoTrack = local?.tracks?.video?.persistentTrack || local?.tracks?.video?.track;
        if (videoTrack) setLocalVideoTrack(videoTrack);
      });

      callObject.on('track-started', (event: any) => {
        if (!event?.participant || !event?.track) return;

        const { participant, track } = event;

        if (participant.local) {
          if (track.kind === 'video') setLocalVideoTrack(track);
        } else {
          if (track.kind === 'video') setRemoteVideoTrack(track);
          if (track.kind === 'audio') setRemoteAudioTrack(track);
          setHasRemoteParticipant(true);
        }
      });

      callObject.on('track-stopped', (event: any) => {
        if (!event?.participant) return;

        const { participant, track } = event;

        if (participant.local) {
          if (track?.kind === 'video') setLocalVideoTrack(null);
        } else {
          if (track?.kind === 'video') setRemoteVideoTrack(null);
          if (track?.kind === 'audio') setRemoteAudioTrack(null);
        }
      });

      callObject.on('participant-joined', (event: any) => {
        if (event?.participant && !event.participant.local) {
          setHasRemoteParticipant(true);
          const vTrack = event.participant.tracks?.video?.persistentTrack;
          const aTrack = event.participant.tracks?.audio?.persistentTrack;
          if (vTrack) setRemoteVideoTrack(vTrack);
          if (aTrack) setRemoteAudioTrack(aTrack);
        }
      });

      callObject.on('participant-left', (event: any) => {
        if (event?.participant && !event.participant.local) {
          setRemoteVideoTrack(null);
          setRemoteAudioTrack(null);
          setHasRemoteParticipant(false);
        }
      });

      callObject.on('left-meeting', () => {
        window.close();
      });

      callObject.on('error', (error: any) => {
        antdMessage.error('Erro ao conectar: ' + (error?.errorMsg || 'Erro desconhecido'));
        setLoading(false);
      });

      callObject.on('app-message', (event: any) => {
        if (event?.data?.type === 'chat') {
          const message: ChatMessage = {
            sender: event.data.senderName || 'Participante',
            content: event.data.message,
            timestamp: event.data.timestamp || new Date().toISOString(),
            fromSelf: event.fromId === 'local',
          };

          setChatMessages((prev) => [...prev, message]);

          if (!chatOpen && event.fromId !== 'local') {
            setUnreadCount((prev) => prev + 1);
          }
        }
      });

      await callObject.join({ startVideoOff: true, startAudioOff: true });
      await new Promise(resolve => setTimeout(resolve, 1000));
      callObject.setLocalVideo(true);
      callObject.setLocalAudio(true);
    } catch {
      setError('Erro ao conectar na chamada');
      setLoading(false);
    }
  };

  const handleLeave = useCallback(async () => {
    if (dailyCallRef.current) {
      await dailyCallRef.current.leave();
    }
    window.close();
  }, []);

  const handleToggleMic = useCallback(() => {
    if (!dailyCallRef.current) return;
    const newMuted = !isMicMuted;
    dailyCallRef.current.setLocalAudio(!newMuted);
    setIsMicMuted(newMuted);
  }, [isMicMuted]);

  const handleToggleCamera = useCallback(() => {
    if (!dailyCallRef.current) return;
    const newOff = !isCameraOff;
    dailyCallRef.current.setLocalVideo(!newOff);
    setIsCameraOff(newOff);
  }, [isCameraOff]);

  const handleSendMessage = async () => {
    const message = chatInput.trim();
    if (!message || !dailyCallRef.current || sending) return;

    try {
      setSending(true);

      await dailyCallRef.current.sendAppMessage(
        {
          type: 'chat',
          message: message,
          senderName: guestName,
          timestamp: new Date().toISOString(),
        },
        '*'
      );

      setChatMessages((prev) => [
        ...prev,
        {
          sender: guestName,
          content: message,
          timestamp: new Date().toISOString(),
          fromSelf: true,
        },
      ]);

      setChatInput('');
    } catch {
      antdMessage.error('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFlipCamera = async () => {
    if (!dailyCallRef.current) {
      antdMessage.warning('Aguarde a conexão ser estabelecida');
      return;
    }

    try {
      const { device } = await dailyCallRef.current.cycleCamera();

      if (!device) {
        antdMessage.warning('Apenas uma câmera disponível neste dispositivo');
        return;
      }

      const local = dailyCallRef.current.participants().local;
      const localTrack =
        local?.tracks?.video?.persistentTrack || local?.tracks?.video?.track;
      const settings = localTrack?.getSettings?.();
      const labelLc = (device.label || '').toLowerCase();
      const isBack =
        settings?.facingMode === 'environment' ||
        /back|rear|traseir|environment/.test(labelLc);
      const newFacing = isBack ? 'environment' : 'user';

      setSelectedCamera(newFacing);
      antdMessage.success(`Câmera alterada para ${newFacing === 'user' ? 'frontal' : 'traseira'}`);
    } catch {
      antdMessage.error('Erro ao inverter câmera');
    }
  };

  if (error) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Erro</h2>
          <p className="text-gray-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col bg-gray-900">
      {/* Header */}
      <div className="bg-blue-600 text-white px-4 md:px-6 py-3 md:py-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-lg md:text-xl font-semibold truncate">Vistoria - Banco Bari</h1>
            <p className="text-xs md:text-sm text-blue-100 truncate">Participante: {guestName}</p>
          </div>
          {/* Botão de chat mobile */}
          {isMobile && (
          <Button
            className="ml-2"
            buttonColor="primary"
            icon={<MessageOutlined />}
            onClick={() => {
              setChatOpen(true);
              setUnreadCount(0);
            }}
            style={{ position: 'relative' }}
          >
            Chat
            {unreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
                style={{ fontSize: '10px' }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Video Container */}
        <div className="flex-1 relative bg-black" ref={dailyContainerRef}>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
              <Spin size="large" tip="Conectando..." />
            </div>
          )}

          {/* Hidden audio element for remote audio playback */}
          <audio ref={remoteAudioRef} autoPlay playsInline />

          {isMobile ? (
            /* ===== MOBILE LAYOUT: Local grande + Remote pip ===== */
            <div className="relative w-full h-full">
              {/* LOCAL video = GRANDE (fullscreen) */}
              {localVideoTrack ? (
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{ transform: selectedCamera === 'user' ? 'scaleX(-1)' : 'none' }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                  <div className="text-center">
                    <UserOutlined style={{ fontSize: 48, marginBottom: 8 }} />
                    <p>Câmera desligada</p>
                  </div>
                </div>
              )}

              {/* REMOTE video = pip pequeno (canto superior direito) */}
              <div
                className="absolute top-4 right-4 rounded-lg overflow-hidden shadow-lg z-20"
                style={{
                  width: 112,
                  height: 160,
                  border: '2px solid white',
                  display: hasRemoteParticipant ? 'block' : 'none',
                }}
              >
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-contain bg-black"
                />
                {!remoteVideoTrack && (
                  <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                    <UserOutlined className="text-white text-2xl" />
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* ===== DESKTOP LAYOUT: 50/50 side by side ===== */
            <div className="flex w-full h-full gap-1">
              {/* Local video - 50% */}
              <div className="flex-1 relative bg-gray-900">
                {localVideoTrack ? (
                  <video
                    ref={localVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                    style={{ transform: selectedCamera === 'user' ? 'scaleX(-1)' : 'none' }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-800 text-white">
                    <div className="text-center">
                      <UserOutlined style={{ fontSize: 48, marginBottom: 8 }} />
                      <p>Câmera desligada</p>
                    </div>
                  </div>
                )}
                <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                  {guestName} (Você)
                </div>
              </div>
              {/* Remote video - 50% */}
              <div className="flex-1 relative bg-gray-900">
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{ display: remoteVideoTrack ? 'block' : 'none' }}
                />
                {!remoteVideoTrack && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-800 text-white">
                    <div className="text-center">
                      <UserOutlined style={{ fontSize: 48, marginBottom: 8 }} />
                      <p>{hasRemoteParticipant ? 'Câmera desligada' : 'Aguardando participante...'}</p>
                    </div>
                  </div>
                )}
                {hasRemoteParticipant && (
                  <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                    Vistoriador
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Barra de controles flutuante */}
          {!loading && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3 z-30">
              <button
                onClick={handleToggleMic}
                className={`w-12 h-12 rounded-full flex items-center justify-center text-white transition-colors ${
                  isMicMuted ? 'bg-red-500' : 'bg-gray-700 bg-opacity-80 hover:bg-gray-600'
                }`}
                title={isMicMuted ? 'Ativar microfone' : 'Desativar microfone'}
              >
                {isMicMuted ? <AudioMutedOutlined style={{ fontSize: 20 }} /> : <AudioOutlined style={{ fontSize: 20 }} />}
              </button>
              <button
                onClick={handleToggleCamera}
                className={`w-12 h-12 rounded-full flex items-center justify-center text-white transition-colors ${
                  isCameraOff ? 'bg-red-500' : 'bg-gray-700 bg-opacity-80 hover:bg-gray-600'
                }`}
                title={isCameraOff ? 'Ligar câmera' : 'Desligar câmera'}
              >
                <VideoCameraOutlined style={{ fontSize: 20 }} />
              </button>
              <button
                onClick={handleLeave}
                className="w-12 h-12 rounded-full flex items-center justify-center text-white bg-red-600 hover:bg-red-700 transition-colors"
                title="Sair da chamada"
              >
                <PhoneOutlined style={{ fontSize: 20, transform: 'rotate(135deg)' }} />
              </button>
            </div>
          )}
        </div>

        {/* Chat Container - Desktop (hidden on mobile) */}
        {!isMobile && (
        <div className="hidden md:flex md:w-96 bg-white flex-col border-l border-gray-200">
          {/* Chat Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-800">Chat</h2>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {chatMessages.length === 0 ? (
              <div className="text-center text-gray-400 mt-8">
                <p>Nenhuma mensagem ainda</p>
                <p className="text-sm mt-1">Inicie a conversa!</p>
              </div>
            ) : (
              chatMessages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex flex-col ${msg.fromSelf ? 'items-end' : 'items-start'}`}
                >
                  <div className="text-xs font-semibold text-gray-600 mb-1">
                    {msg.sender}
                    {msg.fromSelf && ' (você)'}
                  </div>
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 ${
                      msg.fromSelf
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-800'
                    }`}
                  >
                    <p className="text-sm break-words">{msg.content}</p>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {new Date(msg.timestamp).toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="flex gap-2">
              <Input.TextArea
                value={chatInput}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setChatInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Digite sua mensagem..."
                maxLength={500}
                autoSize={{ minRows: 1, maxRows: 3 }}
                disabled={sending}
              />
              <Button
                buttonColor="primary"
                icon={<SendOutlined />}
                onClick={handleSendMessage}
                loading={sending}
                disabled={!chatInput.trim()}
              >
                Enviar
              </Button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Chat Drawer - Mobile */}
      {isMobile && (
      <Drawer
        title="Chat"
        placement="bottom"
        onClose={() => setChatOpen(false)}
        open={chatOpen}
        height="70vh"
      >
        <div className="flex flex-col h-full">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto space-y-3 mb-4">
            {chatMessages.length === 0 ? (
              <div className="text-center text-gray-400 mt-8">
                <p>Nenhuma mensagem ainda</p>
                <p className="text-sm mt-1">Inicie a conversa!</p>
              </div>
            ) : (
              chatMessages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex flex-col ${msg.fromSelf ? 'items-end' : 'items-start'}`}
                >
                  <div className="text-xs font-semibold text-gray-600 mb-1">
                    {msg.sender}
                    {msg.fromSelf && ' (você)'}
                  </div>
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 ${
                      msg.fromSelf
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-800'
                    }`}
                  >
                    <p className="text-sm break-words">{msg.content}</p>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {new Date(msg.timestamp).toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex gap-2">
              <Input.TextArea
                value={chatInput}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setChatInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Digite sua mensagem..."
                maxLength={500}
                autoSize={{ minRows: 1, maxRows: 3 }}
                disabled={sending}
              />
              <Button
                buttonColor="primary"
                icon={<SendOutlined />}
                onClick={handleSendMessage}
                loading={sending}
                disabled={!chatInput.trim()}
                size="large"
              >
                {' '}
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
      )}

      {/* Floating Button para inverter câmera (apenas mobile) */}
      {isMobile && !loading && (
        <FloatButton
          icon={<SwapOutlined />}
          tooltip="Inverter câmera (frontal/traseira)"
          onClick={handleFlipCamera}
          type="primary"
          style={{
            right: 24,
            bottom: 100,
            width: 50,
            height: 50,
          }}
        />
      )}
    </div>
  );
}
